import { createContext, useContext, useCallback, useRef, useEffect } from 'react';

/**
 * Configuration du TTL (Time To Live) par type de données
 * - user: données utilisateur, rafraîchies périodiquement
 * - static: données qui ne changent que rarement (procédures, etc.)
 * - default: durée par défaut
 */
const TTL_CONFIG = {
    user: 5 * 60 * 1000, // 5 minutes
    settings: 10 * 60 * 1000, // 10 minutes
    company: 15 * 60 * 1000, // 15 minutes
    collaborators: 10 * 60 * 1000, // 10 minutes
    folders: 5 * 60 * 1000, // 5 minutes
    procedures: 15 * 60 * 1000, // 15 minutes
    static: 30 * 60 * 1000, // 30 minutes
    default: 5 * 60 * 1000, // 5 minutes
};

/**
 * @typedef {Object} CacheEntry
 * @property {unknown} data - Les données cachées
 * @property {number} timestamp - Timestamp de mise en cache
 * @property {string} [category] - Catégorie pour le TTL
 */

/**
 * @typedef {Object} ApiCacheContextValue
 * @property {function(string, unknown, string=): void} setCache - Mettre en cache
 * @property {function(string, string=): unknown|null} getCache - Récupérer du cache
 * @property {function(string): boolean} hasValidCache - Vérifier si le cache est valide
 * @property {function(string=): void} invalidateCache - Invalider le cache
 * @property {function(): void} clearAllCache - Vider tout le cache
 * @property {function(): void} forceRefresh - Forcer le rafraîchissement de tout
 * @property {function(string): void} setReportReturnTo - Définir le chemin de retour pour les rapports
 * @property {function(): string|null} getReportReturnTo - Récupérer le chemin de retour
 * @property {function(): void} clearReportReturnTo - Effacer le chemin de retour
 */

const ApiCacheContext = createContext(null);

/**
 * Provider pour le cache API global
 * Conserve les données en mémoire jusqu'à :
 * - Rechargement de page
 * - Invalidation manuelle (après mutation)
 * - Expiration du TTL
 * - Commande de refresh (pull-to-refresh, F5, etc.)
 */
export function ApiCacheProvider({ children }) {
    /** @type {React.MutableRefObject<Map<string, CacheEntry>>} */
    const cacheRef = useRef(new Map());
    const refreshCounterRef = useRef(0);

    // Chemin de retour pour la navigation depuis les pages rapport
    const reportReturnToRef = useRef(null);

    /**
     * Mettre des données en cache
     * @param {string} key - Clé unique (ex: 'user', 'company:123', 'folders')
     * @param {unknown} data - Données à cacher
     * @param {string} [category='default'] - Catégorie pour déterminer le TTL
     */
    const setCache = useCallback((key, data, category = 'default') => {
        cacheRef.current.set(key, {
            data,
            timestamp: Date.now(),
            category,
            refreshCounter: refreshCounterRef.current,
        });
    }, []);

    /**
     * Récupérer des données du cache si elles sont encore valides
     * @param {string} key - Clé unique
     * @param {string} [category] - Catégorie (optionnel, utilisé pour override le TTL)
     * @returns {unknown|null} - Les données ou null si invalides/absentes
     */
    const getCache = useCallback((key, category) => {
        const entry = cacheRef.current.get(key);
        if (!entry) return null;

        // Vérifier si un refresh global a été demandé
        if (entry.refreshCounter !== refreshCounterRef.current) {
            cacheRef.current.delete(key);
            return null;
        }

        const ttlCategory = category || entry.category || 'default';
        const ttl = TTL_CONFIG[ttlCategory] || TTL_CONFIG.default;
        const age = Date.now() - entry.timestamp;

        if (age > ttl) {
            cacheRef.current.delete(key);
            return null;
        }

        return entry.data;
    }, []);

    /**
     * Vérifier si une entrée de cache est valide
     * @param {string} key - Clé unique
     * @param {string} [category] - Catégorie pour le TTL
     * @returns {boolean}
     */
    const hasValidCache = useCallback(
        (key, category) => {
            return getCache(key, category) !== null;
        },
        [getCache],
    );

    /**
     * Invalider le cache (une clé spécifique ou par pattern)
     * @param {string} [pattern] - Pattern de clé (ex: 'folders' invalide 'folders', 'folders:123', etc.)
     */
    const invalidateCache = useCallback((pattern) => {
        if (!pattern) {
            cacheRef.current.clear();
            return;
        }

        // Supprimer toutes les clés qui commencent par le pattern
        for (const key of cacheRef.current.keys()) {
            if (key === pattern || key.startsWith(`${pattern}:`)) {
                cacheRef.current.delete(key);
            }
        }
    }, []);

    /**
     * Vider tout le cache
     */
    const clearAllCache = useCallback(() => {
        cacheRef.current.clear();
    }, []);

    /**
     * Forcer le rafraîchissement de toutes les données
     * Appelé lors d'un pull-to-refresh ou refresh manuel
     */
    const forceRefresh = useCallback(() => {
        refreshCounterRef.current += 1;
        cacheRef.current.clear();
    }, []);

    /**
     * Définir le chemin de retour pour les pages rapport
     * @param {string} path - Le chemin vers lequel retourner
     */
    const setReportReturnTo = useCallback((path) => {
        reportReturnToRef.current = path;
    }, []);

    /**
     * Récupérer le chemin de retour
     * @returns {string|null}
     */
    const getReportReturnTo = useCallback(() => {
        return reportReturnToRef.current;
    }, []);

    /**
     * Effacer le chemin de retour
     */
    const clearReportReturnTo = useCallback(() => {
        reportReturnToRef.current = null;
    }, []);

    // Écouter les événements de refresh (F5, pull-to-refresh custom, etc.)
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl+R ou F5 ou Cmd+R
            if ((e.ctrlKey && e.key === 'r') || e.key === 'F5' || (e.metaKey && e.key === 'r')) {
                forceRefresh();
            }
        };

        // Événement custom pour pull-to-refresh sur mobile
        const handleCustomRefresh = () => {
            forceRefresh();
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('horodaty:refresh', handleCustomRefresh);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('horodaty:refresh', handleCustomRefresh);
        };
    }, [forceRefresh]);

    const value = {
        setCache,
        getCache,
        hasValidCache,
        invalidateCache,
        clearAllCache,
        forceRefresh,
        setReportReturnTo,
        getReportReturnTo,
        clearReportReturnTo,
    };

    return <ApiCacheContext.Provider value={value}>{children}</ApiCacheContext.Provider>;
}

/**
 * Hook pour accéder au cache API
 * @returns {ApiCacheContextValue}
 */
export function useApiCache() {
    const context = useContext(ApiCacheContext);
    if (!context) {
        throw new Error('useApiCache must be used within an ApiCacheProvider');
    }
    return context;
}

/**
 * Hook utilitaire pour récupérer des données avec cache
 * @param {string} cacheKey - Clé de cache unique
 * @param {function(): Promise<unknown>} fetcher - Fonction pour récupérer les données
 * @param {Object} [options]
 * @param {string} [options.category='default'] - Catégorie pour le TTL
 * @param {boolean} [options.enabled=true] - Activer/désactiver le fetch
 * @param {function(unknown): void} [options.onSuccess] - Callback après succès
 * @param {function(Error): void} [options.onError] - Callback après erreur
 */
export function useCachedQuery(cacheKey, fetcher, options = {}) {
    const { category = 'default', enabled = true, onSuccess, onError } = options;
    const { getCache, setCache } = useApiCache();

    const fetchWithCache = useCallback(
        async (forceRefresh = false) => {
            // Vérifier le cache sauf si forceRefresh
            if (!forceRefresh) {
                const cached = getCache(cacheKey, category);
                if (cached !== null) {
                    onSuccess?.(cached);
                    return cached;
                }
            }

            try {
                const data = await fetcher();
                setCache(cacheKey, data, category);
                onSuccess?.(data);
                return data;
            } catch (error) {
                onError?.(error);
                throw error;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cacheKey, category, fetcher],
    );

    return {
        fetch: fetchWithCache,
        getCached: () => getCache(cacheKey, category),
        enabled,
    };
}
